// Deep content localizer.
// Translations are keyed by the English SOURCE string (see ./es.js).
// If a Spanish string is missing, the English source is returned (safe fallback)
// and the miss is logged once so gaps can be completed toward 100% coverage.
import { ES } from "@/i18n/es";

// Keys whose string values must NEVER be translated (URLs, identifiers, code, etc.).
const SKIP_KEYS = new Set([
  "path", "to", "href", "type", "icon", "src", "image", "cover_image", "pdf_url",
  "slug", "lang", "doi", "code", "version", "updated", "datePublished", "dateModified",
  "id", "group_id", "tone", "related",
]);

const _missing = new Set();
function logMissing(str) {
  if (process.env.NODE_ENV !== "production" && !_missing.has(str)) {
    _missing.add(str);
    // eslint-disable-next-line no-console
    console.debug("[i18n] missing ES translation:", str);
  }
}

export function translateString(str, lang) {
  if (lang !== "es" || typeof str !== "string" || !str.trim()) return str;
  const hit = ES[str];
  if (hit != null) return hit;
  logMissing(str);
  return str; // English fallback
}

// Recursively localize a content object/array, skipping non-translatable keys.
export function deepLocalize(node, lang, parentKey) {
  if (lang !== "es") return node;
  if (typeof node === "string") {
    return SKIP_KEYS.has(parentKey) ? node : translateString(node, lang);
  }
  if (Array.isArray(node)) return node.map((n) => deepLocalize(n, lang, parentKey));
  if (node && typeof node === "object") {
    // Preserve React components / functions (e.g. lucide icons) untouched.
    if (typeof node === "function" || node.$$typeof) return node;
    const out = {};
    for (const [k, v] of Object.entries(node)) {
      out[k] = SKIP_KEYS.has(k) ? v : deepLocalize(v, lang, k);
    }
    return out;
  }
  return node;
}
